import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import EPageHeader from '@/components/everridge/EPageHeader';
import ESearchInput from '@/components/everridge/ESearchInput';
import ApartmentsCard from '@/features/apartments/components/ApartmentsCard/ApartmentsCard';
import ApartmentsCardMobile from '@/features/apartments/components/ApartmentsCard/ApartmentsCardMobile';
import ApartmentsCardSkeleton from '@/features/apartments/components/ApartmentsCard/ApartmentsCardSkeleton';
import ApartmentsPageEmpty from '@/features/apartments/components/ApartmentsPageEmpty';
import ApartmentsDetails from '@/features/apartments/components/ApartmentsDetails/ApartmentsDetails';
import NewOrder from '@/features/orders/components/NewOrder/NewOrder';
import { useApartmentsListApi } from '@/features/apartments/hooks/useApartmentsListApi';
import ReservationNew from '../reservations/components/ReservationManage';
import useMediaQuery from '@/hooks/useMediaQuery';
import { AppConfig } from '@/config/app.config';
import ApartmentsCardMobileSkeleton from '@/features/apartments/components/ApartmentsCard/ApartmentsCardMobileSkeleton';
import { useUserRole } from '@/features/identity/hooks/useUserRole.ts';
import { cn } from '@/lib/utils';
import Hotel_01_Icon from '@/components/icons/Hotel_01_Icon';
import User_02_Icon from '@/components/icons/User_02_Icon';
import { Roles } from '@/@types/auth.types';

const ApartmentsPage = () => {
    const [apartmentId, setApartmentId] = useState('');
    const [apartmentIdName, setApartmentName] = useState('');
    const [showApartmentsDetails, setShowApartmentsDetails] = useState(false);
    const [showApartmentsNewOrder, setShowApartmentsNewOrder] = useState(false);
    const [showNewReservation, setShowNewReservation] = useState(false);
    const [searchText, setSearchText] = useState('');

    const userRole = useUserRole();
    const isMobile = useMediaQuery(AppConfig.mediaQueries.mobile);
    const loaderRef = useRef<HTMLDivElement | null>(null);
    const { t } = useTranslation();

    const {
        apartmentsListData,
        apartmentsTotalCount,
        ownersTotalCount,
        isLoadingApartmentsListData,
        fetchApartmentsListDataNextPage,
        hasApartmentsListDataNextPage,
        isFetchingApartmentsListDataNextPage,
    } = useApartmentsListApi({ searchText });

    useEffect(() => {
        if (
            !loaderRef.current ||
            !hasApartmentsListDataNextPage ||
            isFetchingApartmentsListDataNextPage
        )
            return;

        const scrollableContainer = loaderRef.current.closest('.overflow-y-scroll');

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    fetchApartmentsListDataNextPage();
                }
            },
            {
                root: scrollableContainer,
                rootMargin: '500px',
            },
        );

        const current = loaderRef.current;
        observer.observe(current);

        return () => observer.unobserve(current);
    }, [
        fetchApartmentsListDataNextPage,
        hasApartmentsListDataNextPage,
        isFetchingApartmentsListDataNextPage,
    ]);

    const handleShowDetails = (id: string) => {
        setApartmentId(id);
        setShowApartmentsDetails(true);
    };

    const handleNewReservation = (id: string) => {
        setApartmentId(id);
        setShowNewReservation(true);
    };

    const handleNewOrder = (id: string, name?: string) => {
        setApartmentId(id);
        setApartmentName(name ?? '');
        setShowApartmentsNewOrder(true);
    };

    return (
        <section className={'flex-1 flex flex-col gap-6'}>
            <EPageHeader
                title={t('main.apartments.title')}
                search={
                    <ESearchInput
                        value={searchText}
                        setValue={setSearchText}
                        placeholder={t('main.apartments.form.search.placeholder')}
                        containerClassName={cn(!isMobile && 'w-60')}
                    />
                }
            >
                {userRole === Roles.admin && (
                    <div className="flex border border-popover-100 rounded-full overflow-hidden">
                        <div className="px-4 py-2.5 flex justify-center items-center gap-1.5 border border-popover-1000 rounded-full bg-popover-1000">
                            <Hotel_01_Icon className="stroke-popover-10 shrink-0" />
                            <div className="min-w-7 text-sm text-popover-10 font-medium leading-none">
                                {apartmentsTotalCount || 0}
                            </div>
                        </div>
                        <div className="px-4 py-2.5 flex justify-center items-center gap-1.5 border border-popover-10 rounded-full bg-popover-10">
                            <User_02_Icon className="stroke-popover-1000 shrink-0" />
                            <div className="min-w-7 text-sm text-popover-1000 font-medium leading-none">
                                {ownersTotalCount || 0}
                            </div>
                        </div>
                    </div>
                )}
            </EPageHeader>
            {!!isLoadingApartmentsListData && (
                <div className="h-fit grid gap-4 grid-cols-[repeat(auto-fill,minmax(300px,1fr))]">
                    {Array.from({ length: 20 }).map((_, index) =>
                        isMobile ? (
                            <ApartmentsCardMobileSkeleton key={index} footerButtons={userRole === Roles.owner ? userRole : undefined} />
                        ) : (
                            <ApartmentsCardSkeleton key={index} footerButtons={userRole === Roles.owner ? userRole : undefined} />
                        ),
                    )}
                </div>
            )}
            {!isLoadingApartmentsListData && !!apartmentsListData?.length && (
                <div className="h-fit grid gap-4 grid-cols-[repeat(auto-fill,minmax(300px,1fr))]">
                    {apartmentsListData.map((apartment) =>
                        isMobile ? (
                            <ApartmentsCardMobile
                                key={apartment.id}
                                {...apartment}
                                showDetails={handleShowDetails}
                                setNewReservation={handleNewReservation}
                                setNewOrder={handleNewOrder}
                                footerButtons={userRole === Roles.owner ? userRole : undefined}
                            />
                        ) : (
                            <ApartmentsCard
                                key={apartment.id}
                                {...apartment}
                                showDetails={handleShowDetails}
                                setNewReservation={handleNewReservation}
                                setNewOrder={handleNewOrder}
                                footerButtons={userRole === Roles.owner ? userRole : undefined}
                            />
                        ),
                    )}
                </div>
            )}
            {!isLoadingApartmentsListData && !apartmentsListData?.length && <ApartmentsPageEmpty />}
            <div ref={loaderRef} className="h-px" />
            {showApartmentsDetails && (
                <ApartmentsDetails
                    id={apartmentId}
                    onClose={() => setShowApartmentsDetails(false)}
                />
            )}
            {showApartmentsNewOrder && (
                <NewOrder
                    apartmentData={{ id: apartmentId, name: apartmentIdName }}
                    onClose={() => setShowApartmentsNewOrder(false)}
                />
            )}
            {showNewReservation && (
                <ReservationNew
                    apartmentId={apartmentId}
                    onClose={() => setShowNewReservation(false)}
                />
            )}
        </section>
    );
};

export default ApartmentsPage;
